import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { createOrganisation, listOrganisations, type Organisation, type OrganisationFields } from "../../api/organisations";
import { orgRefusal } from "./orgRefusal";

/**
 * Organisations: the universities and companies that sponsor service on this
 * hotspot. An operator creates one, approves the plans its sponsor may give,
 * sets its limits and invites its sponsor by mobile; the sponsor then uploads
 * the member list on its own screen.
 *
 * 🔑 Several organisations per namespace, unique names, suspended rather than
 * deleted -- the member rows are the record of who was given access (owner,
 * design §13.1).
 *
 * Design: radius-wiki wiki/sponsor-roster-console-design.md §13.5.
 */
export function OrganisationsScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ["organisations", ns],
    queryFn: async () => (await listOrganisations(identity, ns)).data,
    enabled: !!ns,
  });

  if (!ns) {
    return <Alert severity="error">No namespace could be resolved from your sign-in, so there are no organisations to show.</Alert>;
  }
  const orgs = list.data?.organisations ?? [];

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Organisations</Typography>
        <Button variant="contained" onClick={() => setCreating(true)}>New organisation</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Organisations that sponsor service at <b>{ns}</b>. Each gets a sponsor login you invite by mobile, the plans you approve,
        and the limits you set; its sponsor then uploads the people it pays for.
      </Typography>

      {list.isLoading && <Typography variant="body2">Loading…</Typography>}
      {list.isError && <Alert severity="error">{orgRefusal(list.error)}</Alert>}
      {list.data && orgs.length === 0 && (
        <Typography variant="body2" color="text.secondary">No organisations yet. Create one to invite its sponsor.</Typography>
      )}
      {orgs.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Plans</TableCell>
                <TableCell>Limits</TableCell>
                <TableCell align="right">Sponsors</TableCell>
                <TableCell align="right">Members</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((o) => (
                <TableRow key={o.organisation_id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={`/roster/organisations/${o.organisation_id}`}>{o.name}</Link>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={o.status === "active" ? "success" : "default"} label={o.status} />
                  </TableCell>
                  <TableCell align="right">{o.approved_plans?.length ?? 0}</TableCell>
                  <TableCell>{limitsSummary(o)}</TableCell>
                  <TableCell align="right">
                    {o.sponsor_count}
                    {o.pending_invite_count > 0 && ` + ${o.pending_invite_count} invited`}
                  </TableCell>
                  <TableCell align="right" title={`${o.members.pending} not signed up · ${o.members.activated} signed up · ${o.members.revoked} revoked`}>
                    {o.members.total}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {creating && <CreateDialog ns={ns} onClose={() => setCreating(false)} />}
    </Box>
  );
}

export function limitsSummary(o: Pick<Organisation, "limits">): string {
  if (!o.limits) return "not set";
  return `${o.limits.max_speed_mbps} Mbps · ${o.limits.max_valid_days} days · until ${o.limits.latest_valid_until}`;
}

export const DEFAULT_LABELS = { member_ref_label: "Member ID", group_label_label: "Group", location_label_label: "Location" };

function CreateDialog({ ns, onClose }: { ns: string; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [f, setF] = useState<OrganisationFields>({ name: "", ...DEFAULT_LABELS });
  const create = useMutation({
    mutationFn: async () => (await createOrganisation(identity, ns, { ...f, name: f.name.trim() })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["organisations", ns] });
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New organisation</DialogTitle>
      <DialogContent>
        <OrganisationFieldsForm value={f} onChange={setF} />
        {create.isError && <Alert severity="error" sx={{ mt: 2 }}>{orgRefusal(create.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!f.name.trim() || create.isPending} onClick={() => create.mutate()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Name plus the three labels the sponsor's roster columns carry. */
export function OrganisationFieldsForm({ value, onChange }: { value: OrganisationFields; onChange: (f: OrganisationFields) => void }) {
  const set = (k: keyof OrganisationFields) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [k]: e.target.value });
  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField label="Name" value={value.name} onChange={set("name")} required slotProps={{ htmlInput: { maxLength: 128 } }} autoFocus />
      <Typography variant="body2" color="text.secondary">
        What the sponsor&apos;s list calls three of its columns, for example Roll number, Course and Hostel room.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField label="Member ID column" value={value.member_ref_label} onChange={set("member_ref_label")} slotProps={{ htmlInput: { maxLength: 32 } }} fullWidth />
        <TextField label="Group column" value={value.group_label_label} onChange={set("group_label_label")} slotProps={{ htmlInput: { maxLength: 32 } }} fullWidth />
        <TextField label="Location column" value={value.location_label_label} onChange={set("location_label_label")} slotProps={{ htmlInput: { maxLength: 32 } }} fullWidth />
      </Stack>
    </Stack>
  );
}
